import gql from 'graphql-tag';

// Reconnected to constlending-api-next: `session` replaces the legacy `initializeAuth` (the "me").
export const SESSION = gql`
  query session {
    session {
      user {
        id
        email
        firstName
        lastName
        isAdmin
      }
    }
  }
`;

export const GET_INVESTMENTS = gql`
  query getInvestments(
    $page: Float!
    $count: Float!
    $status: String
    $noteType: String
    $sortBy: InvestmentSortField
    $sortDirection: SortDirection
    $q: String
  ) {
    getInvestments(
      page: $page
      count: $count
      status: $status
      noteType: $noteType
      sortBy: $sortBy
      sortDirection: $sortDirection
      q: $q
    ) {
      _id
      address
      investmentAmount
      termLength
      propertyType
      imageUrl
      city
      state
      zipCode
      createdAt
      updatedAt
      maturityDate
      status
      noteType
      noteListing
      altoRegistered
      hasLedgerActivity
    }
  }
`;

// Same filters as GET_INVESTMENTS so the pagination total matches the list being shown.
export const INVESTMENT_COUNT = gql`
  query investmentCount($noteType: String, $status: String, $q: String) {
    investmentCount: adminInvestmentCount(
      noteType: $noteType
      status: $status
      q: $q
    )
  }
`;

// Reconnected to constlending-api-next. DROPPED (not on the new `Investment`): amount, term, ltv,
// equityIsin, equityRegistered, equityActivityId, funds, transactions. The note's movements no
// longer nest here: they come from the root query ADMIN_INVESTMENT_TRANSACTIONS below.
export const GET_INVESTMENT = gql`
  query getInvestment($investmentId: String!) {
    getInvestment(investmentId: $investmentId) {
      _id
      fundedAmount
      address
      investorYieldMaturity
      investorYieldMaturityInfo
      borrowerPaymentInsurance
      borrowerPaymentInsuranceInfo
      servicingFee
      netYield
      investmentAmount
      investmentAmountInfo
      notePosition
      notePositionInfo
      asIsLoanToValue
      arvLoanToValue
      repaymentType
      termLength
      termLengthInfo
      loanPurpose
      loanPurposeInfo
      exitFee
      exitFeeInfo
      borrowerCreditScore
      propertyType
      numberOfUnits
      asIsValuePurchasePrice
      asIsLtv
      rehabBudget
      rehabBudgetInfo
      loanStatus
      status
      arv
      arvLtv
      riskRating
      riskRatingInfo
      dealOverview
      originalLoanTerm
      originalLoanTermInfo
      extensionOption
      extensionOptionInfo
      extensionFee
      extensionFeeInfo
      prepaymentPenalty
      prepaymentPenaltyInfo
      imageUrl
      city
      state
      zipCode
      termPeriod
      startDate
      nextCycleId
      previousCycleId
      createdAt
      updatedAt
      maturityDate
      fundingAmount
      noteListing
      performance
      documents {
        _id
        name
        url
        investmentId
        isAttached
        createdAt
        updatedAt
      }
      typedTags {
        _id
        name
        type
        color
        createdAt
        updatedAt
      }
      noteType
      altoRegistered
      hasLedgerActivity
      yieldHistory {
        netYield
        changedAt
        changedBy
      }
      borrowerTrackRecord
      paymentGuaranteeFee
      updates {
        _id
        type
        info
        date
        createdAt
      }
    }
  }
`;

// Movements of one note (the Transactions modal in PropertyDetails). Root query, `page` is 1-based,
// the backend rejects `count` > 100. `user` / `userAccount` are null for movements without an
// account (e.g. Alto Invest); the email lives on `user`.
export const ADMIN_INVESTMENT_TRANSACTIONS = gql`
  query adminInvestmentTransactions(
    $investmentId: ID!
    $page: Int
    $count: Int
  ) {
    adminInvestmentTransactions(
      investmentId: $investmentId
      page: $page
      count: $count
    ) {
      total
      page
      count
      items {
        id
        type
        status
        amount
        createdAt
        user {
          id
          email
          firstName
          lastName
        }
        userAccount {
          id
          accountName
          type
        }
      }
    }
  }
`;

// `role` splits the people who USE the platform from the ones who OPERATE it (`isAdmin`), which `type`
// could never do (prod: 3.561 of 3.562 are `investor`). Left OUT of the variables it means everybody —
// which is what the two pickers that share this document need (they look users up by id, admins included).
export const INVESTORS = gql`
  query adminUsers(
    $q: String
    $page: Int
    $count: Int
    $sortBy: AdminUserSortField
    $sortDirection: SortDirection
    $investmentState: AdminUserInvestmentState
    $role: AdminUserRole
  ) {
    adminUsers(
      q: $q
      page: $page
      count: $count
      sortBy: $sortBy
      sortDirection: $sortDirection
      investmentState: $investmentState
      role: $role
    ) {
      total
      page
      count
      items {
        id
        email
        firstName
        lastName
        createdAt
        kycStatus
        investmentCount
        hasPendingInvestment
        hasCompletedInvestment
      }
    }
  }
`;

// Reconnected to constlending-api-next `adminUser`. `getUserByAdmin { individualAccount, entityAccounts }`
// becomes a flat `accounts` list (sorted individual-first on screen via `type`); `kycFails` (accordion)
// becomes each account's `kycReasons`; `ssn`/`dob` no longer exist. `accreditation` is North Capital,
// derived from the individual account. `_id` -> `id`.
// TODO(adminUserTransactions): the per-user `transactionItems` table has no backend op — 4001 exposes
// `adminUserTransactions(userId: ID!, investmentId: ID!)`, which is per INVESTMENT, not per user.
export const INVESTOR = gql`
  query adminUser($userId: ID!) {
    adminUser(userId: $userId) {
      id
      email
      firstName
      lastName
      phoneNumber
      address
      city
      state
      zip
      isAdmin
      isEmailVerified
      type
      altoUserId
      createdAt
      updatedAt
      hasPendingInvestment
      hasCompletedInvestment
      accreditation {
        status
        verificationUrl
        verifiedAt
      }
      accounts {
        id
        type
        accountName
        isDefault
        firstName
        lastName
        email
        kyc
        kycReasons
        kycSyncedAt
        verificationUuid
        isCertified
        ein
        investmentAccountType
        accreditedInvestorStatus
        accreditedAt
        createdAt
        updatedAt
      }
    }
  }
`;

export const TAGS = gql`
  query tags {
    tags {
      _id
      name
      type
      color
      createdAt
      updatedAt
    }
  }
`;

// The positions of ONE user (RETOOL-GUIDE §11.6): one row per note the user put money into, soonest
// maturity first, no pagination. `investedAmount` sums every rail (equity included); `name` may be
// empty when the note no longer exists in `investment` — a data defect the operator must see.
export const ADMIN_USER_INVESTMENTS = gql`
  query adminUserInvestments($userId: ID!) {
    adminUserInvestments(userId: $userId) {
      investmentId
      name
      netYield
      maturityDate
      loanStatus
      investedAmount
      repaidAmount
      balance
      interestEarned
      noteType
      noteListing
    }
  }
`;

// Investment rules (RETOOL-GUIDE §5.1): the STORED row per product — both tiers unresolved, presets,
// default, last edit — which is what the edit form loads. Never the public `investmentRules`: that one
// returns the minimum already resolved for the caller (the admin's own tier and exceptions).
export const ADMIN_INVESTMENT_RULES = gql`
  query adminInvestmentRules {
    adminInvestmentRules {
      productType
      firstTimeMinimum
      subsequentMinimum
      presets
      defaultAmount
      updatedAt
    }
  }
`;

// Per-investor exceptions in force (RETOOL-GUIDE §6.0): one row per person x product, newest grant
// first, backend pagination (`count` <= 100). `userId` is the PERSON's id — what both exception
// mutations take, never an account id; `grantedBy` is an admin user id (resolved via `adminUsers(q)`).
export const ADMIN_INVESTMENT_MINIMUMS = gql`
  query adminInvestmentMinimums($page: Int, $count: Int, $userId: ID) {
    adminInvestmentMinimums(page: $page, count: $count, userId: $userId) {
      total
      page
      count
      items {
        userId
        userAccountId
        email
        firstName
        lastName
        productType
        minimum
        grantedAt
        grantedBy
        reason
      }
    }
  }
`;

// Which deployment the console is talking to, and the ONLY thing that changes per deployment in the
// create-an-admin form. `suggestedAdminPhone` is the shared test handset an environment offers for the
// second factor (sandbox: the code lands in the team's Slack); it is null in dev and in production,
// where each admin enrols their own phone. A prefill, never a permission: the backend keeps deciding.
export const ADMIN_ENVIRONMENT = gql`
  query adminEnvironment {
    adminEnvironment {
      name
      suggestedAdminPhone
    }
  }
`;

// Treasury (2026-09-17): Constitution's own Sila wallet — the balance is LIVE from Sila (never a sum of
// our rows), the banks are the ones Sila lists for the treasury handle, and the movements are ours.
// `wallet` and `banks` come back null when Sila cannot be reached; the movements still render.
export const ADMIN_TREASURY = gql`
  query adminTreasury($page: Int, $count: Int) {
    adminTreasury(page: $page, count: $count) {
      handle
      wallet {
        walletId
        nickname
        balance
        availableBalance
        pendingBalance
      }
      banks {
        accountName
        accountNumber
        accountStatus
        accountType
        providerName
        preferred
        label
        bankAccountId
        connectionBroken
        canRepairConnection
        usable
      }
      movements {
        total
        page
        count
        items {
          id
          direction
          amount
          bankAccountName
          status
          reason
          adminId
          transactionId
          createdAt
          settledAt
          failureReason
          investmentId
          noteName
        }
      }
    }
  }
`;

// House banks (2026-10-29): a Plaid Link token for the treasury. Without `bankAccountId` it starts a
// NEW link; with it, Plaid opens in UPDATE MODE to re-authenticate that bank. The token is minted per
// session and expires, so it is read network-only right before Link opens.
export const ADMIN_TREASURY_BANK_LINK_TOKEN = gql`
  query adminTreasuryBankLinkToken($bankAccountId: ID) {
    adminTreasuryBankLinkToken(bankAccountId: $bankAccountId)
  }
`;

// The treasury's banks alone (no movements refresh, no wallet read): what a picker outside the Treasury
// page needs, in a fraction of the time of `adminTreasury`.
export const ADMIN_TREASURY_BANKS = gql`
  query adminTreasuryBanks {
    adminTreasuryBanks {
      accountName
      accountNumber
      accountStatus
      accountType
      providerName
      preferred
      label
      bankAccountId
      connectionBroken
      canRepairConnection
      usable
    }
  }
`;

// Note wallet (2026-09-18): the LIVE balance of ONE note's Sila wallet (it hangs under the treasury
// handle). Same shape as the treasury's wallet; null when Sila cannot be reached or the note has none.
export const ADMIN_INVESTMENT_WALLET = gql`
  query adminInvestmentWallet($investmentId: String!) {
    adminInvestmentWallet(investmentId: $investmentId) {
      walletId
      nickname
      balance
      availableBalance
      pendingBalance
    }
  }
`;

// The list's Funded column (2026-10-30): the LIVE Sila balance of each note's wallet, for the ids of ONE
// page (≤ 25). Each id answers a number or an `unavailable` reason — never a silent $0.
export const ADMIN_NOTE_WALLET_BALANCES = gql`
  query adminNoteWalletBalances($investmentIds: [ID!]!) {
    adminNoteWalletBalances(investmentIds: $investmentIds) {
      investmentId
      balance
      availableBalance
      pendingBalance
      unavailable
    }
  }
`;
